import { useQuery } from '@apollo/client';
import { Navigate } from 'react-router-dom';

import { OutlinedCard } from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    GetBasicPrescriberOrdersDocument,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import type { BasicPrescriberOrderFragment } from '../../../util/orders/prescriber-order';
import { sortOrdersByDate } from '../../../util/orders/sort-orders-by-date';
import { ConditionalComponent } from '../../utility/ConditionalComponent';
import { MedicineCabinetCardHeader } from '../cabinet/MedicineCabinetCardHeader';
import { MedicineCabinetPlaceholder } from '../cabinet/MedicineCabinetPlaceholder';
import { MedicationOrders } from '../cabinet/orders-card/MedicationOrders';

import { HomeMedCabNoOrders } from './HomeMedCabNoOrders';

type HomeMedCabCardProps = {
    patientId: string;
};

export const HomeMedCabCard = ({ patientId }: HomeMedCabCardProps) => {
    const input = {
        statusFilter: [PrescriberOrderStatus.New, PrescriberOrderStatus.OnHold],
    };

    const { data, loading } = useQuery(GetBasicPrescriberOrdersDocument, {
        variables: { patientId: patientId, input },
        errorPolicy: 'all',
    });

    if (loading) return <MedicineCabinetPlaceholder variant="outlined" />;

    if (!data) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    const medications: BasicPrescriberOrderFragment[] = [];

    data?.patient?.prescriberOrdersConnection.edges.forEach((edge) => {
        medications.push(getFragment(getFragment(edge.node)));
    });

    data?.patient?.unclaimedPrescriberOrdersConnection.edges.forEach((edge) => {
        medications.push(getFragment(edge.node));
    });

    data?.patient?.consentedPatientsConnection.edges.forEach((patientEdge) => {
        patientEdge.node.prescriberOrdersConnection.edges.forEach((edge) => {
            medications.push(getFragment(getFragment(edge.node)));
        });
    });

    const orders = sortOrdersByDate(medications);
    const count = orders.length;

    return (
        <OutlinedCard
            header={<MedicineCabinetCardHeader count={count} />}
            role="list"
        >
            <ConditionalComponent condition={count === 0}>
                <HomeMedCabNoOrders />
            </ConditionalComponent>

            <ConditionalComponent condition={count > 0}>
                <MedicationOrders orders={orders} />
            </ConditionalComponent>
        </OutlinedCard>
    );
};
